import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Box, Button, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faChevronDown,
    faChevronUp,
    faRemove,
} from '@fortawesome/free-solid-svg-icons';

// Imports from Template UI project
import {
    selectProductFilters,
    selectSearchValue,
    selectVehicleApplicationClicked,
    updateisMultiSelectFiltersApplied,
    updateMultiSelectFilters,
} from 'srs.sharedcomponents/lib/esm/redux/slices/productlistSlice';
import { useAppSelector } from 'srs.sharedcomponents/lib/esm/redux/hooks';
import { useTranslate } from 'srs.sharedcomponents/lib/esm/hooks/useTranslate';

// Imports from Rotunda UI project
import { getColor, updateSelections } from '../../MultiSelectList';
import { extractFilterData } from '../../FiltersDataExtraction';
import { CategorySection } from '../../multiSelectComponents/collapsableCategoryMenu/categoryMenu';
import { isFacetTrimingCatalogMatch } from '../../../../../Utils/facetTrimingCatalogValidator';
import { SubFiltersMenuProps, VehicleData } from './SubFiltersMenu.types';
import {
    ActionButton,
    findParentCategory,
    SelectedValues,
} from '../../AdditionalFiltersMenuComponent';

export const SubFiltersMenu: React.FC<SubFiltersMenuProps> = ({
    totalItems,
    selectedValues,
    setSelectedValues,
    updatedFilterItems,
    handleApplyFiltersButton,
    data,
    setData,
    setSearchedValueLabel,
    selectedValuesChanged,
    setIsApplyButtonVisible,
    isApplyButtonVisible,
    isInitialPageLoad,
}) => {
    const [expanded, setExpanded] = useState<{ [key: string]: boolean }>({});
    const [showAll, setShowAll] = useState({
        makes: false,
        years: false,
        models: false,
        components: false,
        category: false,
        supplier: false,
    });
    const [expandedCategories, setExpandedCategories] = useState<{
        [key: string]: boolean;
    }>({});
    const dispatch = useDispatch();
    const { translate } = useTranslate();
    const isVehicleApplicationClicked = useAppSelector(
        selectVehicleApplicationClicked
    );
    const searchedValue = useAppSelector(selectSearchValue);
    const selectedproductfilters = useAppSelector(selectProductFilters);
    const categoriesEEST = ['Make', 'Year', 'Model', 'Component'];
    const {
        makes,
        allYears,
        allModels,
        allComponents,
        allCategoriesWithChildren,
        allInitialCategoriesWithChildren,
        allSuppliers,
    } = extractFilterData(updatedFilterItems, totalItems);

    const { search } = useLocation();
    const searchParams = new URLSearchParams(search);
    let subSearch = searchParams.get('subSearch');
    const tagValue = searchParams.get('TAG');
    const categoryValue = searchParams.get('isCategory');
    const searchValue = searchParams.get('search');
    const isSupplierPage = !!(
        tagValue &&
        tagValue !== 'On Sale' &&
        !categoryValue &&
        !searchValue
    );

    useEffect(() => {
        if (!isVehicleApplicationClicked && !subSearch) {
            dispatch(updateMultiSelectFilters(null));
            dispatch(updateisMultiSelectFiltersApplied(false));
        }
    }, [searchedValue]);

    useEffect(() => {
        setExpanded({});
        setShowAll({
            makes: false,
            years: false,
            models: false,
            components: false,
            category: false,
            supplier: false,
        });
        setExpandedCategories({});
        setData({
            make: {},
            model: {},
            year: {},
            component: {},
        });
        setSelectedValues({
            make: [],
            year: [],
            model: [],
            component: [],
            supplier: isSupplierPage ? [tagValue] : [],
            category: [],
        });
        setSearchedValueLabel((prev: any) => ({
            ...prev,
            make: '',
            year: '',
            model: '',
            component: '',
            supplier: '',
            category: tagValue && !isSupplierPage ? tagValue : '',
        }));
    }, [searchedValue]);

    useEffect(() => {
        if (searchedValue) {
            searchedValueLabels(searchedValue);
        }
    }, [totalItems]);

    function countAllSuppliers(
        selectedSupplierValues: any,
        allSuppliers: string | string[]
    ) {
        if (!selectedSupplierValues || !Array.isArray(allSuppliers)) {
            return 0;
        }

        const count = selectedSupplierValues.filter((supplier: string) =>
            allSuppliers.includes(supplier)
        ).length;

        return count;
    }

    const toggleSection = (section: any) => {
        setExpanded((prev) => ({ ...prev, [section]: !prev[section] }));
    };

    const toggleShowMoreCategories = (label: string) => {
        setExpandedCategories((prev) => ({
            ...prev,
            [label]: !prev[label],
        }));
    };

    const toggleShowMore = (section: keyof typeof showAll) => {
        setShowAll((prev) => ({ ...prev, [section]: !prev[section] }));
    };

    const handleSelectAllClearAllButtonsForCategory = (
        Categorylabel: string,
        allCategoriesData: {
            label: string;
            items: any;
            code: string;
        }[],
        selectedValues: SelectedValues
    ) => {
        const parentCategoryDetails = findCategoryChildrenByCode(
            allCategoriesData,
            Categorylabel
        );

        const allChildCodes = parentCategoryDetails?.items
            ? parentCategoryDetails?.items.flatMap((item: any) =>
                  item.items &&
                  Array.isArray(item.items) &&
                  item.items.length > 0
                      ? item.items.map((subItem: any) => subItem.code)
                      : [item.code]
              )
            : [];
        const allChildrenSelected = allChildCodes.every((childCode: string) =>
            selectedValues['category'].includes(childCode)
        );

        const noChildrenSelected = allChildCodes.every(
            (childCode: string) =>
                !selectedValues['category'].includes(childCode)
        );

        let selectionState;
        if (allChildCodes.length === 0) {
            selectionState = 'no Button';
        } else if (allChildrenSelected) {
            selectionState = 'allSelected';
        } else if (noChildrenSelected) {
            selectionState = 'noneSelected';
        } else {
            selectionState = 'someSelected';
        }

        return {
            selectionState,
        };
    };

    const findCategoryChildrenByCode = (categories: any, code: string): any => {
        for (let category of categories) {
            if (category.code === code) {
                return category;
            }
            if (category.items && category.items.length > 0) {
                const found = findCategoryChildrenByCode(category.items, code);
                if (found) {
                    return found;
                }
            }
        }
        return null;
    };

    const handleSelectAllClearAllButtonsForRestSections = (
        type: 'make' | 'model' | 'year' | 'component' | 'supplier',
        allValues: any
    ) => {
        const allChildCodes = allValues;
        const allChildrenSelected = allChildCodes.every((childCode: string) =>
            selectedValues[type].includes(childCode)
        );

        const noChildrenSelected = allChildCodes.every(
            (childCode: string) => !selectedValues[type].includes(childCode)
        );

        let selectionState;
        if (allChildCodes.length === 0) {
            selectionState = 'no Button';
        } else if (allChildrenSelected) {
            selectionState = 'allSelected';
        } else if (noChildrenSelected) {
            selectionState = 'noneSelected';
        } else {
            selectionState = 'someSelected';
        }

        return {
            selectionState,
        };
    };

    const handleCategorySelection = (
        Categorylabel: string,
        select: boolean,
        allCategoriesData: {
            label: string;
            items: any;
            code: string;
        }[]
    ) => {
        const parentCategoryDetails = findCategoryChildrenByCode(
            allCategoriesData,
            Categorylabel
        );
        const allChildCodes = parentCategoryDetails?.items
            ? parentCategoryDetails?.items.flatMap((item: any) =>
                  item.items &&
                  Array.isArray(item.items) &&
                  item.items.length > 0
                      ? item.items.map((subItem: any) => subItem.code)
                      : [item.code]
              )
            : [];

        setSelectedValues((prevState) => {
            let updatedCategoryCodes = select
                ? [
                      ...prevState.category,
                      ...(allChildCodes.length > 0
                          ? allChildCodes
                          : [parentCategoryDetails?.code]),
                  ]
                : [
                      ...prevState.category.filter(
                          (code) => !allChildCodes.includes(code)
                      ), 
                      ...(parentCategoryDetails?.code
                          ? [parentCategoryDetails.code]
                          : []),
                  ];
            updatedCategoryCodes = Array.from(new Set(updatedCategoryCodes));

            const isFacetTrimingCatalog = isCategoryMatch(allChildCodes);

            if (isFacetTrimingCatalog) {
                let makes: string[] = [],
                    years: string[] = [],
                    models: string[] = [],
                    components: string[] = [];

                updatedCategoryCodes.forEach((code: string) => {
                    const codeLevels = code.split('_');

                    if (code.includes('Make')) {
                        makes.push(`${codeLevels[0]} > ${codeLevels[2]}`);
                    } else if (code.includes('Year')) {
                        years.push(`${codeLevels[0]} > ${codeLevels[2]}`);
                    } else if (code.includes('Model')) {
                        models.push(`${codeLevels[0]} > ${codeLevels[2]}`);
                    } else if (code.includes('Component')) {
                        components.push(`${codeLevels[0]} > ${codeLevels[2]}`);
                    }
                });

                return {
                    ...prevState,
                    make: makes,
                    year: years,
                    model: models,
                    component: components,
                    category: updatedCategoryCodes,
                };
            }

            return {
                ...prevState,
                category: updatedCategoryCodes,
            };
        });
    };

    const hasMatchingCategory = (child: string): boolean => {
        return categoriesEEST.some((category) => child.includes(category));
    };

    function isCategoryMatch(children: string[]): boolean {
        return children.some(hasMatchingCategory);
    }

    const handleResetsectionsSelection = (
        allValues: string[],
        select: boolean,
        type: 'make' | 'model' | 'year' | 'component' | 'supplier'
    ) => {
        allValues.forEach(async (value: string) => {
            const isValueSelected = selectedValues[type]?.includes(value);
            if ((select && !isValueSelected) || (!select && isValueSelected)) {
                await handleItemClick(type, value);
            }
        });
    };

    const extractLastLabels = (items: any[]) => {
        let labels: any[] = [];
        items.forEach(
            (item: { items: string | any[]; label: any; code: string }) => {
                if (item.items && item.items.length > 0) {
                    if (Array.isArray(item.items)) {
                        labels = labels.concat(extractLastLabels(item.items));
                    }
                } else {
                    labels.push(item.code);
                }
            }
        );
        return labels;
    };

    const findCategoryCode = (
        node: { items?: { label: string; code?: string }[] } | undefined,
        pathParts: string
    ): string | null => {
        if (!node || pathParts.length === 0) return null;
        for (const item of node.items || []) {
            if (item.label === pathParts) {
                return item.code ?? null;
            }
        }
        return null;
    };

    const searchedValueLabels = (searchedValue: string | null) => {
        if (searchedValue != null && searchedValue != undefined) {
            const parentMake = makes.find(
                (make: string) => make === searchedValue
            );
            const parentYear = allYears.find(
                (year: string) => year === searchedValue
            );
            const parentModel = allModels.find(
                (model: string) => model === searchedValue
            );
            const parentComponent = allComponents.find(
                (component: string) => component === searchedValue
            );
            const parentSupplier = allSuppliers.find(
                (supplier: string) => supplier === searchedValue
            );
            const parentCategory = findParentCategory(
                allCategoriesWithChildren,
                searchedValue ?? ''
            );
            if (parentCategory) {
                const reactAppFacetTrimingCatalog =
                    process.env.REACT_APP_FACET_TRIMING_CATALOG ?? '';
                if (
                    isFacetTrimingCatalogMatch(
                        [parentCategory.label],
                        reactAppFacetTrimingCatalog
                    )
                ) {
                    setSelectedValues((prevState) => {
                        let updatedValues = {
                            make: [...prevState.make],
                            year: [...prevState.year],
                            model: [...prevState.model],
                            component: [...prevState.component],
                            category: [...prevState.category],
                        };

                        const filterCategories = [
                            { key: 'make', selectedKey: 'Makes', index: 0 },
                            { key: 'year', selectedKey: 'Years', index: 1 },
                            { key: 'model', selectedKey: 'Models', index: 2 },
                            {
                                key: 'component',
                                selectedKey: 'Components',
                                index: 3,
                            },
                        ];

                        for (const filter of filterCategories) {
                            const selectedItems =
                                selectedproductfilters?.[filter.selectedKey] ??
                                [];
                            if (selectedItems.length) {
                                updatedValues[
                                    filter.key as keyof typeof updatedValues
                                ].push(...selectedItems);
                                const categoryNode =
                                    allCategoriesWithChildren[0].items[
                                        filter.index
                                    ];
                                const part = selectedItems[0].split(' > ')[1];
                                const categoryCode =
                                    findCategoryCode(categoryNode, part) ??
                                    null;
                                if (categoryCode !== null) {
                                    updatedValues.category.push(categoryCode);
                                }
                            }
                        }
                        updatedValues = {
                            make: Array.from(new Set(updatedValues.make)),
                            year: Array.from(new Set(updatedValues.year)),
                            model: Array.from(new Set(updatedValues.model)),
                            component: Array.from(
                                new Set(updatedValues.component)
                            ),
                            category: Array.from(
                                new Set(updatedValues.category)
                            ),
                        };

                        return { ...prevState, ...updatedValues };
                    });
                } else {
                    setSearchedValueLabel((prevState: any) => ({
                        ...prevState,
                        category: parentCategory.label,
                    }));
                }
            } else if (parentSupplier) {
                setSelectedValues((prevState) => {
                    const newSuppliers = [...prevState.supplier, searchedValue];

                    return {
                        ...prevState,
                        supplier: [...new Set(newSuppliers)], // To remove duplicated values
                    };
                });
            } else if (
                parentMake ||
                parentYear ||
                parentModel ||
                parentComponent
            ) {
                setSelectedValues((prevState) => {
                    let updatedValues = {
                        make: [...prevState.make],
                        year: [...prevState.year],
                        model: [...prevState.model],
                        component: [...prevState.component],
                    };

                    if (selectedproductfilters?.Makes.length) {
                        updatedValues.make.push(
                            ...selectedproductfilters.Makes
                        );
                    }

                    if (selectedproductfilters?.Years.length) {
                        updatedValues.year.push(
                            ...selectedproductfilters.Years
                        );
                    }

                    if (selectedproductfilters?.Models.length) {
                        updatedValues.model.push(
                            ...selectedproductfilters.Models
                        );
                    }
                    if (selectedproductfilters?.Components.length) {
                        updatedValues.component.push(
                            ...selectedproductfilters.Components
                        );
                    } else if (parentMake) {
                        updatedValues.make.push(parentMake);
                    } else if (parentYear) {
                        updatedValues.year.push(parentYear);
                    } else if (parentModel) {
                        updatedValues.model.push(parentModel);
                    } else if (parentComponent) {
                        updatedValues.component.push(parentComponent);
                    }

                    updatedValues = {
                        make: Array.from(new Set(updatedValues.make)),
                        year: Array.from(new Set(updatedValues.year)),
                        model: Array.from(new Set(updatedValues.model)),
                        component: Array.from(new Set(updatedValues.component)),
                    };

                    return { ...prevState, ...updatedValues };
                });
            }
        }
    };

    const handleItemClick: (
        type: 'make' | 'model' | 'year' | 'component' | 'supplier' | 'category',
        value: string,
        parentValue?: string
    ) => Promise<void> = async (
        type: 'make' | 'model' | 'year' | 'component' | 'supplier' | 'category',
        value: string,
        parentValue?: string
    ) => {
        setSelectedValues((prevSelectedValues) => {
            const isSelected = prevSelectedValues[type]?.includes(value);
            const updatedSelectedValues = {
                ...prevSelectedValues,
                [type]: isSelected
                    ? prevSelectedValues[type].filter((item) => item !== value)
                    : [...prevSelectedValues[type], value],
            };

            if (type === 'category') {
                const { selectionState } =
                    handleSelectAllClearAllButtonsForCategory(
                        parentValue ?? '',
                        allInitialCategoriesWithChildren,
                        updatedSelectedValues
                    );
                if (selectionState === 'allSelected') {
                    handleCategorySelection(
                        parentValue ?? '',
                        true,
                        allInitialCategoriesWithChildren
                    );
                } else if (selectionState === 'noneSelected') {
                    handleCategorySelection(
                        parentValue ?? '',
                        false,
                        allInitialCategoriesWithChildren
                    );
                }
            }

            if (type !== 'supplier' && type !== 'category') {
                updateSelections(
                    updatedFilterItems,
                    updatedSelectedValues,
                    setData,
                    makes,
                    allYears,
                    allModels,
                    allComponents
                );
            }

            return updatedSelectedValues;
        });
    };

    function getProductFilterOptionStyles(
        data: VehicleData,
        selectedValues: any,
        dataValue: any,
        itemDataObject: any
    ) {
        const isEmpty = Object.values(data).every(
            (value) => value?.value?.length === 0
        );
        const isSelected = Object.values(selectedValues).some(
            (categoryValues: any) => categoryValues.includes(dataValue)
        );
        const isDataTypeIncluded = Object.values(itemDataObject).some(
            (makes: any) => makes.includes(dataValue)
        );

        let backgroundColor = 'lightgrey';
        let isDisabled = true;

        if (isSelected) {
            backgroundColor = '#066FEF';
            isDisabled = false;
        } else if (
            isDataTypeIncluded ||
            isEmpty ||
            Object.keys(itemDataObject).length == 0
        ) {
            backgroundColor = 'white';
            isDisabled = false;
        }

        return { backgroundColor, isDisabled, isSelected };
    }

    const { selectionState: supplierSelectionState } =
        handleSelectAllClearAllButtonsForRestSections('supplier', allSuppliers);

    return (
        <>
            <Box>
                {allCategoriesWithChildren.length > 0 && (
                    <Box
                        sx={{
                            marginTop: '15px',
                        }}
                    >
                        <CategorySection
                            selectedValues={selectedValues}
                            expanded={expanded}
                            toggleSection={toggleSection}
                            handleItemClick={handleItemClick}
                            items={allCategoriesWithChildren}
                            handleCategorySelection={(
                                value: string,
                                selection: boolean
                            ) =>
                                handleCategorySelection(
                                    value,
                                    selection,
                                    allInitialCategoriesWithChildren
                                )
                            }
                            handleSelectAllClearAllButtons={(value: string) =>
                                handleSelectAllClearAllButtonsForCategory(
                                    value,
                                    allInitialCategoriesWithChildren,
                                    selectedValues
                                )
                            }
                            translate={translate}
                            expandedCategories={expandedCategories}
                            toggleShowMoreCategories={toggleShowMoreCategories}
                        />
                    </Box>
                )}

                <Box id={'supplierMenuOption_Suppliers'}>
                    {allSuppliers.length > 0 && (
                        <Box
                            style={{
                                position: 'relative',
                                width: '100%',
                                borderRadius: '4px',
                                textAlign: 'center',
                                border: '1px solid gray',
                                marginTop: '15px',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '5px 10px',
                                cursor: 'pointer',
                                textTransform: 'none',
                                wordBreak: 'break-word',
                                backgroundColor:
                                    countAllSuppliers(
                                        selectedValues['supplier'],
                                        allSuppliers
                                    ) > 0
                                        ? '#066FEF'
                                        : 'white',
                                color: getColor(
                                    countAllSuppliers(
                                        selectedValues['supplier'],
                                        allSuppliers
                                    ) > 0
                                ),
                            }}
                            onClick={() => toggleSection('supplier')}
                        >
                            {countAllSuppliers(
                                selectedValues['supplier'],
                                allSuppliers
                            ) > 0 && (
                                <Typography
                                    sx={{
                                        border: '1px solid',
                                        borderRadius: '50%',
                                        minWidth: '30px',
                                        minHeight: '30px',
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        textAlign: 'center',
                                        fontSize: '12px',
                                    }}
                                >
                                    {countAllSuppliers(
                                        selectedValues['supplier'],
                                        allSuppliers
                                    )}
                                </Typography>
                            )}

                            <Typography>
                                {translate(`ltl_SupplierFacetText`)}
                            </Typography>
                            <FontAwesomeIcon
                                icon={
                                    expanded.supplier
                                        ? faChevronUp
                                        : faChevronDown
                                }
                            />
                        </Box>
                    )}

                    {(allSuppliers?.length ?? 0) > 0 && expanded.supplier && (
                        <Box
                            style={{
                                marginTop: '10px',
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent:
                                        supplierSelectionState ===
                                        'noneSelected'
                                            ? 'flex-end'
                                            : 'space-between',
                                    marginBottom: '10px',
                                    marginLeft: 'auto',
                                    gap: '10px',
                                    flexWrap: 'wrap',
                                }}
                            >
                                {(() => {
                                    if (
                                        supplierSelectionState === 'no Button'
                                    ) {
                                        return null;
                                    }
                                    if (
                                        supplierSelectionState === 'allSelected'
                                    ) {
                                        return (
                                            <ActionButton
                                                onClick={() =>
                                                    handleResetsectionsSelection(
                                                        allSuppliers,
                                                        false,
                                                        'supplier'
                                                    )
                                                }
                                                buttonType="clearAll"
                                                buttonId={`$supplierMenuOption_clearAll`}
                                            >
                                                {translate(`ltl_ClearAllText`)}{' '}
                                            </ActionButton>
                                        );
                                    } else if (
                                        supplierSelectionState ===
                                        'someSelected'
                                    ) {
                                        return (
                                            <>
                                                <ActionButton
                                                    onClick={() =>
                                                        handleResetsectionsSelection(
                                                            allSuppliers,
                                                            false,
                                                            'supplier'
                                                        )
                                                    }
                                                    buttonType="clearAll"
                                                    buttonId={`$supplierMenuOption_clearAll`}
                                                >
                                                    {translate(
                                                        `ltl_ClearAllText`
                                                    )}{' '}
                                                </ActionButton>
                                                <ActionButton
                                                    onClick={() =>
                                                        handleResetsectionsSelection(
                                                            allSuppliers,
                                                            true,
                                                            'supplier'
                                                        )
                                                    }
                                                    buttonType="selectAll"
                                                    buttonId={`$supplierMenuOption_selectAll`}
                                                >
                                                    {translate('selectAll_txt')}
                                                </ActionButton>
                                            </>
                                        );
                                    } else if (
                                        supplierSelectionState ===
                                        'noneSelected'
                                    ) {
                                        return (
                                            <ActionButton
                                                onClick={() =>
                                                    handleResetsectionsSelection(
                                                        allSuppliers,
                                                        true,
                                                        'supplier'
                                                    )
                                                }
                                                buttonType="selectAll"
                                                buttonId={`$supplierMenuOption_selectAll`}
                                            >
                                                {translate('selectAll_txt')}
                                            </ActionButton>
                                        );
                                    }
                                })()}
                            </Box>
                            {showAll.supplier ? (
                                <Box
                                    style={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: '10px',
                                        maxHeight: '400px',
                                        overflowY: 'auto',
                                        padding: '10px',
                                    }}
                                >
                                    {allSuppliers.map((supplier) => (
                                        <Button
                                            key={supplier}
                                            id={`supplierFilterBtn_${supplier.replaceAll(
                                                ' ',
                                                ''
                                            )}`}
                                            style={{
                                                border: '1px solid gray',
                                                borderRadius: '4px',
                                                padding: '5px 10px',
                                                width: 'auto',
                                                textAlign: 'center',
                                                wordBreak: 'break-word',
                                                backgroundColor: selectedValues[
                                                    'supplier'
                                                ].includes(String(supplier))
                                                    ? '#066FEF'
                                                    : 'white',
                                                cursor: 'pointer',
                                                textTransform: 'none',
                                                color: getColor(
                                                    selectedValues[
                                                        'supplier'
                                                    ].includes(String(supplier))
                                                ),
                                            }}
                                            startIcon={
                                                selectedValues[
                                                    'supplier'
                                                ].includes(supplier) ? (
                                                    <FontAwesomeIcon
                                                        icon={faRemove}
                                                        size="xs"
                                                        style={{
                                                            fontSize: '10px',
                                                        }}
                                                    />
                                                ) : null
                                            }
                                            onClick={() =>
                                                handleItemClick(
                                                    'supplier',
                                                    supplier
                                                )
                                            }
                                        >
                                            {supplier}
                                        </Button>
                                    ))}
                                </Box>
                            ) : (
                                <Box
                                    style={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: '10px',
                                        padding: '0',
                                    }}
                                >
                                    {allSuppliers
                                        .slice(0, 6)
                                        .map((supplier) => (
                                            <Button
                                                key={supplier}
                                                id={`supplierFilterBtn_${supplier.replaceAll(
                                                    ' ',
                                                    ''
                                                )}`}
                                                style={{
                                                    border: '1px solid gray',
                                                    borderRadius: '4px',
                                                    padding: '5px 10px',
                                                    width: 'auto',
                                                    textAlign: 'center',
                                                    wordBreak: 'break-word',
                                                    backgroundColor:
                                                        selectedValues[
                                                            'supplier'
                                                        ].includes(supplier)
                                                            ? '#066FEF'
                                                            : 'white',
                                                    cursor: 'pointer',
                                                    textTransform: 'none',
                                                    color: getColor(
                                                        selectedValues[
                                                            'supplier'
                                                        ].includes(supplier)
                                                    ),
                                                }}
                                                startIcon={
                                                    selectedValues[
                                                        'supplier'
                                                    ].includes(supplier) ? (
                                                        <FontAwesomeIcon
                                                            icon={faRemove}
                                                            size="xs"
                                                            style={{
                                                                fontSize:
                                                                    '10px',
                                                            }}
                                                        />
                                                    ) : null
                                                }
                                                onClick={() =>
                                                    handleItemClick(
                                                        'supplier',
                                                        supplier
                                                    )
                                                }
                                            >
                                                {supplier}
                                            </Button>
                                        ))}
                                </Box>
                            )}
                        </Box>
                    )}

                    {allSuppliers.length > 6 && expanded.supplier && (
                        <Button
                            onClick={() => toggleShowMore('supplier')}
                            style={{
                                marginTop: 1,
                                fontSize: '10px',
                                fontWeight: '900',
                                color: 'rgb(14, 22, 100) !important',
                            }}
                        >
                            {showAll.supplier
                                ? translate(`less_txt`)
                                : translate(`more_txt`)}
                        </Button>
                    )}
                </Box>
            </Box>
            {(selectedValuesChanged || isApplyButtonVisible) &&
                !isInitialPageLoad &&
                updatedFilterItems?.totalCount !== 0 && (
                    <Box>
                        <Button
                            style={{
                                marginTop: '20px',
                                fontSize: '12px',
                                fontWeight: '900',
                                marginLeft: 'auto',
                                textTransform: 'capitalize',
                                color: 'white',
                                backgroundColor: 'rgb(14, 22, 100)',
                                padding: '5px',
                                borderRadius: 5,
                                display: 'flex',
                            }}
                            id="allFilterSection_applyButton"
                            onClick={() => {
                                handleApplyFiltersButton();
                                setIsApplyButtonVisible(false);
                            }}
                        >
                            {translate('apply_txt')}
                        </Button>
                    </Box>
                )}
        </>
    );
};
